import axios from "axios";
import posthog from "posthog-js";
import {Storage} from "../utils/storage.util.js";

const AUTH_TOKEN_KEY = "jhi-authenticationToken";
const AUTH_MODERATION_KEY = "jhi-moderation";

export interface StandardLoginRequest {
  username: string;
  password: string;
  rememberMe: boolean;
}

export interface AdminLoginRequest {
  invitationKey: string;
  invitationOwner: string;
}

export interface AutoLoginRequest {
  invitationKey: string;
}

function clearLocalStorage(): void {
  if (Storage.local.get(AUTH_TOKEN_KEY)) {
    Storage.local.remove(AUTH_TOKEN_KEY);
  }
  if (Storage.session.get(AUTH_TOKEN_KEY)) {
    Storage.session.remove(AUTH_TOKEN_KEY);
  }
  Storage.local.remove(AUTH_MODERATION_KEY);
}

/**
 * Extracts the JWT from the `authorization` response header and persists it.
 *
 * Throws instead of silently succeeding when the token cannot be obtained or
 * stored — in-app browsers (Facebook/Instagram webviews) may strip the header
 * or reject localStorage writes, and a silent failure leaves the user on a
 * login loop with no error (#458).
 */
function extractAndStoreAuthToken(
  authorizationHeader: string | undefined,
  moderation: "true" | "false"
): void {
  if (!authorizationHeader || authorizationHeader.slice(0, 7) !== "Bearer ") {
    throw new Error("Authentication response is missing a valid Bearer token");
  }
  const jwt = authorizationHeader.slice(7);
  if (!jwt) {
    throw new Error("Authentication response is missing a valid Bearer token");
  }
  try {
    Storage.local.set(AUTH_TOKEN_KEY, jwt);
    Storage.local.set(AUTH_MODERATION_KEY, moderation);
  } catch {
    throw new Error("Failed to store the authentication token");
  }
  // Synchronous read-back: catches storages that silently drop writes
  // (does NOT cover ephemeral storage wiped on a later navigation).
  if (Storage.local.get(AUTH_TOKEN_KEY) !== jwt) {
    throw new Error("Failed to store the authentication token");
  }
  syncPosthogCaptureConsent(moderation);
}

/**
 * Flips posthog-js capture consent to match the login kind, immediately —
 * without waiting for the next page load (#625). The `initAnalytics` opt-out
 * only covers pages loaded while the moderation flag is already set; a
 * "Login as" click inside a running SPA never re-runs init, so we sync here.
 *
 * `captureEventName: false` on opt-in avoids polluting the funnel with the
 * default `$opt_in` event. `opt_out_capturing` blocks capture only — feature
 * flag evaluation stays intact, so the admin sees the app exactly as the
 * impersonated user.
 */
function syncPosthogCaptureConsent(moderation: "true" | "false"): void {
  if (moderation === "true") {
    posthog.opt_out_capturing();
  } else {
    posthog.opt_in_capturing({captureEventName: false});
  }
}

export const authenticationApiService = {
  async standardLogin(request: StandardLoginRequest): Promise<void> {
    clearLocalStorage();
    const response = await axios.post<{id_token: string}>("api/authenticate", request);
    extractAndStoreAuthToken(response.headers.authorization, "false");
  },

  async adminLogin(request: AdminLoginRequest): Promise<void> {
    clearLocalStorage();
    const response = await axios.post("api/authenticate", request);
    extractAndStoreAuthToken(response.headers.authorization, "true");
  },

  async autoLogin(request: AutoLoginRequest): Promise<void> {
    clearLocalStorage();
    const response = await axios.post("api/authenticate", request);
    extractAndStoreAuthToken(response.headers.authorization, "false");
  },

  logout(): void {
    clearLocalStorage();
  },

  isAuthenticated(): boolean {
    return !!(Storage.local.get(AUTH_TOKEN_KEY) || Storage.session.get(AUTH_TOKEN_KEY));
  },

  isModeration(): boolean {
    return Storage.local.get(AUTH_MODERATION_KEY) === "true";
  },
};
